import React, { useState } from 'react';
import { CafeOwnerForm } from './CafeOwnerForm'; // Adjust the import path as needed

export function PricingSection() {
  const [subscriberCount, setSubscriberCount] = useState(3);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const isEarlyBird = subscriberCount < 50;

  const plans = [
    {
      name: "Starter",
      tables: 5,
      price: 1999,
      features: [
        "5 tables included",
        "Basic analytics",
        "Email support",
        "Mobile app access"
      ]
    },
    {
      name: "Professional",
      tables: isEarlyBird ? 20 : 15,
      price: 2999,
      features: [
        isEarlyBird ? "20 tables (15 + 5 bonus)" : "15 tables included",
        "Advanced analytics",
        "Priority support",
        "API access",
        "Custom integrations"
      ],
      popular: true,
      earlyBirdBadge: isEarlyBird
    },
    {
      name: "Enterprise",
      tables: "Unlimited",
      price: "Custom",
      features: [
        "Unlimited tables",
        "White-label options",
        "Dedicated account manager",
        "24/7 phone support",
        "Custom development"
      ]
    }
  ];

  const handleGetStarted = (planName: string) => {
    if (planName === "Professional" && isEarlyBird) {
      setSubscriberCount(prev => Math.min(prev + 1, 50));
    }

    // Open the form for all plans except Enterprise (which should contact sales)
    if (planName !== "Enterprise") {
      setIsFormOpen(true);
    } else {
      const phone = "919172237224"; // country code + number (no +)
      const message = "Hello, I’m interested in the Enterprise plan with unlimited tables, white-label options, and custom development. Please contact me with pricing and next steps.";
      const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
      window.open(url, "_blank");
    }
  };

  return (
    <div className="py-20 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">Simple, Transparent Pricing</h2>
          <p className="text-neutral-600 text-lg">
            Choose the plan that fits your needs. All plans include core features with no hidden fees.
          </p>
        </div>

        {isEarlyBird && (
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-4 mb-8 text-center">
            <p className="text-yellow-900 font-medium">
              🎉 Early Bird Special: First 50 subscribers get 5 extra tables on Pro plan! ({50 - subscriberCount} spots left)
            </p>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-3xl p-8 border-2 transition-all hover:shadow-xl ${plan.popular
                  ? 'bg-neutral-900 text-white border-neutral-900 transform scale-105'
                  : 'bg-white border-neutral-200'
                }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-yellow-400 text-neutral-900 px-4 py-1 rounded-full text-xs font-bold">
                  MOST POPULAR
                </div>
              )}
              {plan.earlyBirdBadge && (
                <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                  +5 BONUS TABLES
                </div>
              )}

              <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>

              <div className="mb-6">
                {typeof plan.price === 'number' ? (
                  <>
                    <span className="text-4xl font-bold">₹{plan.price.toLocaleString()}</span>
                    <span className={plan.popular ? 'text-neutral-300' : 'text-neutral-600'}>/month</span>
                  </>
                ) : (
                  <span className="text-4xl font-bold">{plan.price}</span>
                )}
              </div>

              <p className={`text-sm mb-6 font-medium ${plan.popular ? 'text-neutral-300' : 'text-neutral-600'}`}>
                {typeof plan.tables === 'number' ? `${plan.tables} tables` : plan.tables}
              </p>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <svg className={`w-5 h-5 mt-0.5 flex-shrink-0 ${plan.popular ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleGetStarted(plan.name)}
                className={`w-full py-3.5 rounded-full text-sm font-medium transition-all ${plan.popular
                    ? 'bg-white text-neutral-900 hover:bg-neutral-100'
                    : 'border-2 border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white'
                  }`}
              >
                {plan.price === "Custom" ? "Contact Sales" : "Get Started"}
              </button>
            </div>
          ))}
        </div>

        <p className="text-center text-neutral-600 text-sm mt-12">
          All plans include free onboarding and training. Cancel anytime. No questions asked.
        </p>
      </div>

      {/* Cafe Owner Form Modal */}
      <CafeOwnerForm isOpen={isFormOpen} setIsOpen={setIsFormOpen} />
    </div>
  );
}